import * as React from 'react';
import {
    type CallbackNode,
    unstable_cancelCallback as cancelCallback,
    unstable_LowPriority as LowPriority,
    unstable_scheduleCallback as scheduleCallback,
} from 'scheduler';

interface DebounceOptions {
    /**
     * Concurrent execution of callbacks.
     * Default: true
     */
    concurrent?: boolean;

    /**
     * Maximum time to wait before triggering
     * Default: 0.
     */
    maxWait?: number;

    /**
     * If true, the callback will be executed immediately if it's the first call.
     */
    immediate?: boolean;
}

type DeferredCallback<T> = () => T;

type DebounceFn<T> = ((fn: DeferredCallback<T>) => void) & {
    /**
     * Execute without waiting.
     */
    flush: () => T | undefined;

    /**
     * For async debounced functions, return the current execution (if any).
     */
    waitCurrentExecution: () => T extends Promise<any> ? T | undefined : undefined;

    /**
     * Cancel upcoming execution.
     */
    cancel: () => void;
};

/**
 * Create a smart debounced function that will execute the passed callback
 */
export function createDebouncedFlush<T>(
    delay: number,
    options: DebounceOptions = {}
): DebounceFn<T> {
    const { concurrent = true, maxWait = 0 } = options;

    let currentExecution: T | undefined;
    let scheduledNode: CallbackNode | null = null;
    let timeout: NodeJS.Timeout | null = null;
    let pendingExecution: DeferredCallback<T> | null = null;
    let latestExecution: number | null = null;

    // @ts-ignore
    const debounced: DebounceFn<T> = (fn: DeferredCallback<T>) => {
        pendingExecution = () => {
            pendingExecution = null;

            if (!concurrent && currentExecution) {
                return currentExecution;
            }

            const onFinally = () => {
                currentExecution = undefined;
            };

            latestExecution = Date.now();
            const result = fn();

            if (result instanceof Promise) {
                currentExecution = result;
                result.then(onFinally, onFinally);
            }

            scheduledNode = null;
            timeout = null;

            return result;
        };

        debounced.cancel();

        if (
            (maxWait && latestExecution && latestExecution < Date.now() - maxWait) ||
            (options.immediate && !latestExecution)
        ) {
            pendingExecution();
        } else {
            timeout = setTimeout(() => {
                scheduledNode = scheduleCallback(LowPriority, () => {
                    if (pendingExecution) {
                        pendingExecution();
                    }
                });
            }, delay);
        }
    };

    debounced.cancel = () => {
        if (timeout) {
            clearTimeout(timeout);
        }

        if (scheduledNode) {
            cancelCallback(scheduledNode);
        }
    };

    debounced.flush = () => {
        debounced.cancel();

        if (pendingExecution) {
            return pendingExecution();
        }
    };

    // @ts-ignore
    debounced.waitCurrentExecution = () => {
        if (currentExecution) {
            return currentExecution as T extends Promise<any> ? T | undefined : undefined;
        }

        return undefined;
    };

    return debounced;
}

/**
 * Smart debouncer with low priority.
 */
export function useDebouncedFlush<T = any>(
    delay: number,
    options: DebounceOptions = {}
): DebounceFn<T> {
    return React.useMemo(
        () =>
            createDebouncedFlush<T>(delay, {
                concurrent: options.concurrent,
                maxWait: options.maxWait,
                immediate: options.immediate,
            }),
        [delay, options.concurrent, options.maxWait, options.immediate]
    );
}

/**
 * Defer a callback with low priority and a delay. it ensures the callback if flushed before unmounting.
 */
export function useDeferredFlush<T = any>(delay: number, options?: DebounceOptions): DebounceFn<T> {
    const result = useDebouncedFlush<T>(delay, options);

    React.useEffect(() => {
        return () => {
            result.flush();
        };
    }, [result]);

    return result;
}
